import React, { useCallback, useEffect, useState } from 'react';
import MacroMetadata from 'components/common/AlertOrMacroMetadata/AlertOrMacroMetadata';
import { trackMacroTextCopied, SEARCH_ACTION_SOURCES } from '../../../analytics/searchEventAnalytics';
import { trackButtonClick, BUTTONS } from '../../../analytics/buttonEventAnalytics';
import CopyButton from '../../tools/SearchPage/CopyButton';
import DummyButton from '../../tools/SearchPage/DummyButton/DummyButton';
import Linkify from 'linkify-react';
import PropTypes from 'prop-types';
import { CategoryHeadings, MacroHeadingText, MacroContentSection, BoldText } from '../../styled/searchResultPage';
import { CHAT, EMAIL, JIT_ESCALATION } from '../../../constants/macroTypes';
import { shouldCopyMacro, sanitizeHtml, messageToClientFormat } from '../../../util';
import AddCouponCodeButtonContainer from '../../../containers/tools/SearchPage/AddCouponCodeButtonContainer';
import { EscalationMarkerSpan } from '../MWList/MWList.styles';
import { DraftHelpers } from '../../../util/Draft';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import InstructionComponent from 'containers/tools/SearchPage/InstructionsContainer';
import Loading from 'components/LoadingComponent/LoadingComponent';
import get from 'lodash/get';
import { updateUsedEasyConnections } from 'api/easyConnections';
import { handleAsync } from 'util/handleAsync';
import isEmpty from 'lodash/isEmpty';
import MacroText from '../MacroText/MacroText';
import { hasUnresolvedEasyConnections, getActiveTriggersInTextMessage } from '../../../util/macroUtil';
import {
  Container,
  ContentWrapper,
  MacroHeading,
  ActionButtons,
  VersionToggleLink,
  LoadingWrapper,
  HeadingSection,
  BlockingAlert,
} from './MWResultPage.styles';
import {
  extractEasyConnectionDataForSelectedTicket,
  extractNonHistoricalTicketIdFromState,
  extractSelectedInboxTab,
  extractSelectedTicketFromState,
  extractSelectedWizardMacro,
  getDecisionNodeAtPath,
  getNewMessagesEditor,
  isSelectedTicketLiveChat,
  isSelectedTicketTaken,
} from '../../../extractFromState';
import { onPasteText, saveNewMessageEditorState } from '../../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { getEasyConnectionData } from '../../../actions/easyConnectionActions';
import { handleEscalationPopup } from '../../../actions/popupActions';
import { setChatProductRecMacroCopied } from '../../../actions/chatUI';
import { getMLRecommendedProducts } from '../../../actions/creatorsAsync';
import { handleError } from 'services/ErrorService/ErrorService';
import { addEdgeToDecisionTreePath } from '../../../actions/searchActions';
import DTInfoWidget from '../../DecisionTree/DTInfoWidget/DTInfoWidget';

const MWResultPage = (props) => {
  const { searchResult, disableScroll = false, withEdge = false } = props;
  const dummyButtonActionTag = searchResult.dummyButtonActionTags ?? searchResult.dummyButtonActionTag;
  const selectedConversationId = useSelector(extractNonHistoricalTicketIdFromState);
  const selectedInboxTab = useSelector(extractSelectedInboxTab);
  const selectedTicketLiveChat = useSelector(isSelectedTicketLiveChat);
  const selectedTicket = useSelector(extractSelectedTicketFromState);
  const selectedMacro = useSelector(extractSelectedWizardMacro);
  const chatProductRecTrigger = get(selectedMacro, 'chatProductRecsEnabled', false);
  const editorState = useSelector(getNewMessagesEditor);
  const easyConnectionData = useSelector(extractEasyConnectionDataForSelectedTicket);
  const decisionNode = useSelector(getDecisionNodeAtPath);

  const [editableMacroContent, setEditableMacroContent] = useState('');
  const [loadingMLData, setLoadingMLData] = useState(true);
  const [usedEasyConnections, setUsedEasyConnections] = useState([]);
  const [selectedType, setSelectedType] = useState(selectedTicketLiveChat ? CHAT : EMAIL);
  const dispatch = useDispatch();

  const handleEscalation = async (ticket) => {
    try {
      await dispatch(handleEscalationPopup(ticket));
    } catch (error) {
      handleError({ error });
    }
  };

  const getInitialMacroText = useCallback(() => {
    const macroMap = {
      [EMAIL]: searchResult.emailText || searchResult.text,
      [CHAT]: searchResult.chatText || searchResult.text,
      default: '',
    };

    return sanitizeHtml(macroMap[selectedType] || macroMap.default);
  }, [searchResult, selectedType]);

  const handleGetMLData = useCallback(async () => {
    const macroText = getInitialMacroText();

    await Promise.all([
      dispatch(getEasyConnectionData(macroText)),
      dispatch(getMLRecommendedProducts(macroText, chatProductRecTrigger)),
    ]);
    setLoadingMLData(false);
    setEditableMacroContent(macroText);
  }, [dispatch, chatProductRecTrigger, getInitialMacroText]);

  useEffect(() => {
    handleGetMLData();
  }, [handleGetMLData]);

  const isLiveChatAndChatTextAvailable = () => {
    return selectedTicketLiveChat && !!searchResult.chatText;
  };

  const toggleAvailable = () => {
    // checking to see if there is both chat text and email that do not match
    return (
      isLiveChatAndChatTextAvailable() && !!searchResult.emailText && searchResult.emailText !== searchResult.chatText
    );
  };

  const handleClickCopyButton = () => {
    const { ticketType, communicationType, customerPtrId } = selectedTicket;

    // If the macro that was copied contains the trigger for showing product recs, update redux that an eligible macro was copied for this ticket and product recs can be shown in chatUI
    if (chatProductRecTrigger) {
      dispatch(setChatProductRecMacroCopied(true));
    }

    if (shouldCopyMacro(selectedInboxTab)) {
      // copy text to input
      const macro = messageToClientFormat(editableMacroContent, ticketType, communicationType);
      const newEditorState = DraftHelpers.appendHtmlToContents(macro, editorState);
      dispatch(saveNewMessageEditorState(selectedConversationId, newEditorState, chatProductRecTrigger));
      const newContentState = newEditorState.getCurrentContent();
      const raw = convertToRaw(newContentState);
      const newHtml = draftToHtml(raw);
      dispatch(onPasteText(newHtml));

      if (!isEmpty(usedEasyConnections)) {
        // store easy connection data
        handleAsync(() => updateUsedEasyConnections({ customerId: customerPtrId, usedEasyConnections }));
      }

      // analytics
      trackButtonClick(BUTTONS.COPY_MACRO_TEXT);
      trackMacroTextCopied(SEARCH_ACTION_SOURCES.COPY_BUTTON, searchResult);

      if (withEdge) {
        dispatch(addEdgeToDecisionTreePath('copied'));
      }
    }
  };

  const handleReplaceTrigger = ({ updatedMacro, updatedUsedEasyConnections }) => {
    // close popup, track selections for ML
    setEditableMacroContent(updatedMacro);
    setUsedEasyConnections(updatedUsedEasyConnections);
  };

  const { isDummyActionUsed, handleDummyButtonClick: executeDummyAction } = DummyButton.useDummyButton({
    dummyButtonActionTag,
    showLoading: false,
    shouldCreateLastMileRecord: false,
  });

  const handleDummyButtonClick = () => {
    if (shouldCopyMacro(selectedInboxTab)) {
      executeDummyAction();
      // tag ticket with dummy action, and create note on ticket
    }
  };

  const handleTypeChange = () => {
    if (selectedType === CHAT) {
      setSelectedType(EMAIL);
    } else {
      setSelectedType(CHAT);
    }
  };

  const getOppositeType = (type) => {
    return type === CHAT ? 'email' : 'chat';
  };

  const isCopyButtonDisabled =
    hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData) || !isDummyActionUsed();

  return loadingMLData ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <Container disableScroll={disableScroll}>
      {!!decisionNode?.infoWidget && <DTInfoWidget />}
      {searchResult.instructions && <InstructionComponent instructions={searchResult.instructions} />}
      <ContentWrapper>
        <>
          <HeadingSection>
            <MacroMetadata resource={searchResult} theme="dark" />
            <MacroHeading>
              {searchResult.macroType === JIT_ESCALATION && <EscalationMarkerSpan>[ESCALATION]</EscalationMarkerSpan>}
              {`${selectedType} macro`}
            </MacroHeading>
            <CategoryHeadings>
              <MacroHeadingText hide={!searchResult.category}>
                <BoldText>Category &rarr;</BoldText> {searchResult.category}
              </MacroHeadingText>
              <MacroHeadingText hide={!searchResult.description}>
                <BoldText>Description &rarr;</BoldText> {searchResult.description}
              </MacroHeadingText>
              <MacroHeadingText hide={!searchResult.fullSubheading}>
                <BoldText>Title &rarr;</BoldText> <span>{searchResult.fullSubheading}</span>
              </MacroHeadingText>
            </CategoryHeadings>
          </HeadingSection>
          <MacroContentSection>
            <MacroText
              easyConnectionData={easyConnectionData}
              editableMacroContent={editableMacroContent}
              onReplaceTrigger={handleReplaceTrigger}
              disableManualCopy={isCopyButtonDisabled}
            />
          </MacroContentSection>
          <ActionButtons>
            {hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData) && (
              <BlockingAlert>
                Button will activate once hover action for{' '}
                {getActiveTriggersInTextMessage(editableMacroContent, easyConnectionData)} is complete
              </BlockingAlert>
            )}
            <CopyButton
              isSelectedTicketTaken={isSelectedTicketTaken}
              handleEscalation={handleEscalation}
              selectedMacro={selectedMacro}
              selectedTicket={selectedTicket}
              onClick={handleClickCopyButton}
              selectedInboxTab={selectedInboxTab}
              disabled={isCopyButtonDisabled}
              withEdge={withEdge}
            />
            <DummyButton
              disabled={hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData)}
              buttonData={dummyButtonActionTag}
              onClick={handleDummyButtonClick}
              selectedInboxTab={selectedInboxTab}
            />
            {searchResult.couponCodeTypeId && (selectedInboxTab === 'assigned' || selectedInboxTab === 'live_chat') && (
              <AddCouponCodeButtonContainer couponCodeTypeId={searchResult.couponCodeTypeId} />
            )}
          </ActionButtons>
          <CategoryHeadings>
            <MacroHeadingText hide={!searchResult.link}>
              <BoldText>Link:</BoldText> <Linkify options={{ target: '_blank' }}>{searchResult.link}</Linkify>
            </MacroHeadingText>
          </CategoryHeadings>
          {toggleAvailable() && (
            <VersionToggleLink onClick={handleTypeChange}>
              See {getOppositeType(selectedType)} version
            </VersionToggleLink>
          )}
        </>
      </ContentWrapper>
    </Container>
  );
};

export default MWResultPage;

MWResultPage.propTypes = {
  searchResult: PropTypes.object.isRequired,
  withEdge: PropTypes.bool,
};
